import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useApi } from '../lib/api'
import { useCurrentUser } from '../lib/auth'
import type { Invoice, InvoiceStatus } from '../types'
import { INVOICE_STATUS_LABELS } from '../types'
import { PdfThumbnail } from '../components/PdfPreview'
import { InvoiceStateMachine } from '../components/StateMachineVisualizer'

type InvoiceDetail = Invoice & {
  order_reference: string
  client_name: string
  client_email: string
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { apiFetch } = useApi()
  const { user } = useCurrentUser()
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelNote, setCancelNote] = useState('')

  const canManage = user?.roles.some(r => r === 'admin' || r === 'accounting') ?? false

  useEffect(() => {
    apiFetch<InvoiceDetail>(`/api/invoices/${id}`)
      .then(setInvoice)
      .catch(() => setError('Rechnung konnte nicht geladen werden.'))
      .finally(() => setLoading(false))
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleStatusUpdate(status: InvoiceStatus, notes?: string) {
    if (!invoice) return
    setSaving(true)
    try {
      const updated = await apiFetch<Invoice>(`/api/invoices/${invoice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
      })
      setInvoice(prev => prev ? { ...prev, ...updated } : prev)
    } finally {
      setSaving(false)
    }
  }

  function fmtEuro(amount: number) {
    return amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
  }

  if (loading) return <div className="loading">Rechnung wird geladen…</div>
  if (error)   return <div className="error-msg">{error}</div>
  if (!invoice) return null

  const isActive = invoice.status === 'shipped'

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/invoices" style={{ color: 'var(--color-muted)', fontSize: 13 }}>← Rechnungen</Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <h1 style={{ margin: 0 }}>{invoice.invoice_number}</h1>
            <span className={`badge badge-${invoice.status}`} style={{ fontSize: 14 }}>
              {INVOICE_STATUS_LABELS[invoice.status]}
            </span>
          </div>
        </div>
      </div>

      <InvoiceStateMachine currentStatus={invoice.status} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Rechnungsdetails</h2>
            <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px 12px', fontSize: 14 }}>
              <dt style={{ color: 'var(--color-muted)' }}>Rechnungsnummer</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{invoice.invoice_number}</dd>

              <dt style={{ color: 'var(--color-muted)' }}>Auftrag</dt>
              <dd style={{ margin: 0 }}>
                <Link to={`/orders/${invoice.order_id}`}>{invoice.order_reference}</Link>
              </dd>

              <dt style={{ color: 'var(--color-muted)' }}>Kunde</dt>
              <dd style={{ margin: 0 }}>{invoice.client_name}</dd>

              <dt style={{ color: 'var(--color-muted)' }}>E-Mail</dt>
              <dd style={{ margin: 0 }}>{invoice.client_email}</dd>

              <dt style={{ color: 'var(--color-muted)' }}>Betrag (brutto)</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{fmtEuro(invoice.total_amount)}</dd>

              <dt style={{ color: 'var(--color-muted)' }}>Verschickt am</dt>
              <dd style={{ margin: 0 }}>{new Date(invoice.sent_at).toLocaleDateString('de-DE')}</dd>

              {invoice.notes && (
                <>
                  <dt style={{ color: 'var(--color-muted)' }}>Notizen</dt>
                  <dd style={{ margin: 0 }}>{invoice.notes}</dd>
                </>
              )}
            </dl>
          </div>

          {invoice.r2_key && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Rechnungs-PDF</h2>
              <PdfThumbnail
                src={`/api/invoices/${invoice.id}/pdf`}
                label={invoice.invoice_number}
                width={120}
              />
              <div style={{ marginTop: 12 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => window.open(`/api/invoices/${invoice.id}/pdf`, '_blank')}
                >
                  PDF öffnen
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {canManage && isActive && (
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nächster Status</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  className="btn btn-primary"
                  disabled={saving}
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => handleStatusUpdate('paid')}
                >
                  Bezahlt
                </button>
                <button
                  className="btn btn-danger"
                  disabled={saving}
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowCancelModal(true)}
                >
                  Stornieren
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div className="card" style={{ width: 400, margin: 0 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Rechnung stornieren</h2>
            <p style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12 }}>
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <textarea
              placeholder="Grund (optional)"
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={3}
              style={{ width: '100%', marginBottom: 12, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowCancelModal(false); setCancelNote('') }}>
                Abbrechen
              </button>
              <button
                className="btn btn-danger"
                disabled={saving}
                onClick={async () => {
                  await handleStatusUpdate('cancelled', cancelNote || undefined)
                  setShowCancelModal(false)
                  setCancelNote('')
                }}
              >
                Stornieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
